import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Trash2, Image as ImageIcon, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatEUR } from '../../utils/format';

const SeasonalLandingPages = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    custom_text: '',
    image_url: '',
    selected_products: [],
    is_active: true,
    order_index: 0
  });

  useEffect(() => {
    loadPages();
    loadProducts();
  }, []);

  const loadPages = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Fehler beim Laden der Seiten');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/upload-image`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, image_url: data.url });
        toast.success('Bild hochgeladen!');
      } else {
        toast.error('Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fehler beim Hochladen');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Bitte Titel eingeben');
      return;
    }

    if (!formData.image_url.trim()) {
      toast.error('Bitte Bild hochladen');
      return;
    }

    try {
      const url = editingPage
        ? `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/${editingPage.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/create`;

      const response = await fetch(url, {
        method: editingPage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPage ? 'Seite aktualisiert!' : 'Seite erstellt!');
        setShowModal(false);
        setEditingPage(null);
        setFormData({
          title: '',
          description: '',
          custom_text: '',
          image_url: '',
          selected_products: [],
          is_active: true,
          order_index: 0
        });
        loadPages();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      description: page.description || '',
      custom_text: page.custom_text || '',
      image_url: page.image_url,
      selected_products: page.selected_products || [],
      is_active: page.is_active,
      order_index: page.order_index
    });
    setShowModal(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Landing-Page wirklich löschen?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/${pageId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Seite gelöscht!');
        loadPages();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (pageId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/admin/${pageId}/toggle`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Status geändert!');
        loadPages();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const toggleProductSelection = (productId) => {
    const selected = formData.selected_products.includes(productId);
    if (selected) {
      setFormData({
        ...formData,
        selected_products: formData.selected_products.filter(id => id !== productId)
      });
    } else {
      setFormData({
        ...formData,
        selected_products: [...formData.selected_products, productId]
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Saison-Landing-Pages</h1>
          <p className="text-gray-600">Erstellen Sie saisonale Landing-Pages mit eigenen Produkten</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Neue Seite
        </Button>
      </div>

      {/* Pages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pages.map((page) => (
          <Card key={page.id} className="overflow-hidden">
            <div className="aspect-video overflow-hidden bg-gray-100">
              {page.image_url && (
                <img
                  src={page.image_url}
                  alt={page.title}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-lg">{page.title}</h3>
                <Badge variant={page.is_active ? 'default' : 'secondary'}>
                  {page.is_active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </div>
              {page.description && (
                <p className="text-sm text-gray-600 mb-3">{page.description}</p>
              )}
              <p className="text-sm text-gray-500 mb-4">
                {page.selected_products?.length || 0} Produkte ausgewählt
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/seasonal/${page.id}`)}>
                  <Eye className="h-4 w-4 mr-1" />
                  Ansehen
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleEdit(page)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleActive(page.id)}>
                  {page.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(page.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {pages.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <ImageIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="mb-4">Noch keine Landing-Pages erstellt</p>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Erste Seite erstellen
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                {editingPage ? 'Seite bearbeiten' : 'Neue Landing-Page'}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Titel *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Weihnachts-Special"
                  />
                </div>

                <div>
                  <Label>Beschreibung</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Kurze Beschreibung"
                  />
                </div>

                <div>
                  <Label>Custom Text für Landing-Page</Label>
                  <Textarea
                    value={formData.custom_text}
                    onChange={(e) => setFormData({ ...formData, custom_text: e.target.value })}
                    placeholder="Text der auf der Landing-Page angezeigt wird..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label>Bild hochladen *</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Preview" className="mt-2 h-32 rounded" />
                  )}
                </div>
              </div>

              {/* Product Selection */}
              <div>
                <Label className="mb-3 block">Produkte auswählen</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-2 border rounded">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`cursor-pointer p-3 border rounded transition-all ${
                        formData.selected_products.includes(product.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {product.images?.[0] && (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                      )}
                      <p className="text-sm font-medium truncate">{product.name}</p>
                      <p className="text-sm text-gray-600">{formatEUR(product.price)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {formData.selected_products.length} Produkte ausgewählt
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handleSubmit} className="flex-1">
                  {editingPage ? 'Aktualisieren' : 'Erstellen'}
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowModal(false);
                  setEditingPage(null);
                  setFormData({
                    title: '',
                    description: '',
                    custom_text: '',
                    image_url: '',
                    selected_products: [],
                    is_active: true,
                    order_index: 0
                  });
                }}>
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonalLandingPages;
